import {
  appData,
  state,
  getEffectiveViewLayout,
  getBoardViewSettings,
  getViewLayout,
  saveViewSettings,
} from "../../app/state/state.js";
import { applyBrandingVariables } from "../ui/brandingTokens.js";
import {
  CONTROL_ICON_MAP,
  ACTIVE_BORDER_CLASS,
  ACTIVE_TEXT_CLASS,
  applyControlButtonActiveState,
  escapeHtml,
  getItemType,
  getLayoutToggleIcon,
} from "./boardControlDefinitions.js";
import {
  renderActionButton,
  renderControlMenu,
  renderMenuButton,
} from "./boardControlMenus.js";
import {
  clearOpenMenuState,
  getOpenMenuState,
  setFilterMenuState,
  setGroupMenuState,
  setOpenMenuState,
} from "./boardControlState.js";
import {
  getFilterDefinition,
  getFilterValueCount,
  getTotalActiveFilterCount,
} from "./boardRuntime.js";

let controlsInitialised = false;
const searchDebounceTimers = new Map();
let pendingSearchFocusState = null;
let hasMobileLayoutListener = false;
const MOBILE_BOARD_LAYOUT_MEDIA = "(max-width: 767px)";

function clampNumber(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getCollisionBoundary(container) {
  const boundary = container.closest("[data-sandbox-collision-boundary]");
  if (boundary) {
    return boundary.getBoundingClientRect();
  }
  const width = window.innerWidth || document.documentElement.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight;
  return {
    left: 0,
    top: 0,
    right: width,
    bottom: height,
    width,
    height,
  };
}

function positionControlMenu(button) {
  const wrapper = button?.closest("[data-control-wrapper]");
  const menu = wrapper?.querySelector("[data-control-menu]");
  if (!wrapper || !menu || menu.classList.contains("hidden")) {return;}

  const boundary = getCollisionBoundary(wrapper);
  const wrapperRect = wrapper.getBoundingClientRect();
  const triggerRect = button.getBoundingClientRect();
  const menuWidth = Number.parseFloat(menu.style.width) || menu.offsetWidth || 240;
  const gap = 6;
  const padding = 8;

  menu.style.left = "0";
  menu.style.right = "auto";
  menu.style.top = "0";
  menu.style.bottom = "auto";

  const menuHeight = menu.offsetHeight;
  const minLeft = boundary.left + padding;
  const maxLeft = boundary.right - padding - menuWidth;
  const preferredLeft = triggerRect.right - menuWidth;
  const left = clampNumber(preferredLeft, minLeft, Math.max(minLeft, maxLeft));

  const belowTop = triggerRect.bottom + gap;
  const aboveTop = triggerRect.top - gap - menuHeight;
  const belowSpace = boundary.bottom - padding - belowTop;
  const aboveSpace = aboveTop - boundary.top - padding;
  const shouldOpenUp = menuHeight > belowSpace && aboveSpace > belowSpace;
  const minTop = boundary.top + padding;
  const maxTop = boundary.bottom - padding - menuHeight;
  const preferredTop = shouldOpenUp ? aboveTop : belowTop;
  const top = clampNumber(preferredTop, minTop, Math.max(minTop, maxTop));

  menu.style.left = `${left - wrapperRect.left}px`;
  menu.style.top = `${top - wrapperRect.top}px`;
}

function queueControlMenuPosition(button) {
  if (!button) {return;}
  positionControlMenu(button);
  requestAnimationFrame(() => positionControlMenu(button));
}

function positionVisibleControlMenus() {
  document.querySelectorAll("[data-control-menu]:not(.hidden)").forEach((menu) => {
    const button = menu.closest("[data-control-wrapper]")?.querySelector("[data-control-trigger]");
    positionControlMenu(button);
  });
}

function closeAllControlMenus(exceptWrapper = null) {
  if (!exceptWrapper) {
    clearOpenMenuState();
  }
  document.querySelectorAll("[data-control-wrapper]").forEach((wrapper) => {
    if (exceptWrapper && wrapper === exceptWrapper) {return;}
    const button = wrapper.querySelector("[data-control-trigger]");
    const isPersistentActive = button?.dataset.persistentActive === "true";
    applyControlButtonActiveState(button, isPersistentActive);
    wrapper.querySelector("[data-control-menu]")?.classList.add("hidden");
  });
}

function openControlMenu(button) {
  const wrapper = button.closest("[data-control-wrapper]");
  const menu = wrapper?.querySelector("[data-control-menu]");
  if (!wrapper || !menu) {return;}

  const shouldOpen = menu.classList.contains("hidden");
  closeAllControlMenus(wrapper);

  if (!shouldOpen) {
    menu.classList.add("hidden");
    clearOpenMenuState();
    const isPersistentActive = button.dataset.persistentActive === "true";
    applyControlButtonActiveState(button, isPersistentActive);
    return;
  }

  applyControlButtonActiveState(button, true);
  menu.classList.remove("hidden");
  setOpenMenuState({
    viewKey: button.dataset.viewKey,
    menuId: button.dataset.controlTrigger,
  });
  queueControlMenuPosition(button);
}

function rerenderBoard() {
  import("../../app/view/viewController.js").then((module) => {
    module.renderCurrentBoard();
  });
}

function queueSearchFocusRestore(viewKey, searchInput = null) {
  const activeInput = searchInput ||
    document.querySelector(`.board-search-input[data-view-key="${viewKey}"]`);
  if (!activeInput) {
    pendingSearchFocusState = null;
    return;
  }

  pendingSearchFocusState = {
    viewKey,
    selectionStart: activeInput.selectionStart ?? activeInput.value.length,
    selectionEnd: activeInput.selectionEnd ?? activeInput.value.length,
  };
}

function restorePendingSearchFocus(viewKey, controls) {
  if (!pendingSearchFocusState || pendingSearchFocusState.viewKey !== viewKey) {return;}

  const searchInput = controls.querySelector(
    `.board-search-input[data-view-key="${viewKey}"]`,
  );
  if (!searchInput) {
    pendingSearchFocusState = null;
    return;
  }

  requestAnimationFrame(() => {
    searchInput.focus();
    try {
      searchInput.setSelectionRange(
        pendingSearchFocusState.selectionStart,
        pendingSearchFocusState.selectionEnd,
      );
    } catch {
      // Ignore unsupported selection restoration edge cases.
    }
    pendingSearchFocusState = null;
  });
}

function updateLayout(viewKey, layout) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings || settings.layout === layout) {return;}

  clearOpenMenuState();
  settings.layout = layout;
  state.showClosed = false;
  if (viewKey !== "tasks") {
    state.showWIP = false;
  }
  saveViewSettings();
  rerenderBoard();
}

function updateGrouping(viewKey, nextGroupBy) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings) {return;}

  setGroupMenuState(viewKey, nextGroupBy === "date" ? "dateMode" : null);
  if (getEffectiveViewLayout(viewKey) === "table") {
    settings.tableGroupBy = nextGroupBy;
  } else {
    settings.boardGroupBy = nextGroupBy;
  }

  saveViewSettings();
  rerenderBoard();
}

function updateDateGroupingMode(viewKey, nextMode) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings) {return;}

  setGroupMenuState(viewKey, "dateMode");
  if (getEffectiveViewLayout(viewKey) === "table") {
    settings.tableDateGroupingMode = nextMode;
  } else {
    settings.boardDateGroupingMode = nextMode;
  }

  saveViewSettings();
  rerenderBoard();
}

function updateVisibleColumn(viewKey, columnKey, isVisible) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings) {return;}

  const visibleColumns = Array.isArray(settings.visibleColumns) ?
    [...settings.visibleColumns] : [];

  if (isVisible && !visibleColumns.includes(columnKey)) {
    visibleColumns.push(columnKey);
  }

  if (!isVisible) {
    settings.visibleColumns = visibleColumns.filter((key) => key !== columnKey);
  } else {
    settings.visibleColumns = visibleColumns;
  }

  setOpenMenuState({ viewKey, menuId: "columns" });
  saveViewSettings();
  rerenderBoard();
}

function updateSort(viewKey, sortKey) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings) {return;}
  setOpenMenuState({ viewKey, menuId: "sort" });
  if (settings.sortBy === sortKey) {
    settings.sortDirection = settings.sortDirection === "asc" ? "desc" : "asc";
  } else {
    settings.sortBy = sortKey;
    settings.sortDirection = "asc";
  }
  saveViewSettings();
  rerenderBoard();
}

function clearSort(viewKey, { keepMenuOpen = true } = {}) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings) {return;}
  setOpenMenuState(keepMenuOpen ? { viewKey, menuId: "sort" } : null);
  settings.sortBy = null;
  settings.sortDirection = "asc";
  saveViewSettings();
  rerenderBoard();
}

function updateFilterValues(viewKey, filterKey, values) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings) {return;}
  if (!settings.filters) {settings.filters = {};}

  if (getFilterValueCount(values, getFilterDefinition(viewKey, filterKey)) === 0) {
    delete settings.filters[filterKey];
  } else {
    settings.filters[filterKey] = values;
  }

  saveViewSettings();
  setFilterMenuState(viewKey, filterKey);
  rerenderBoard();
}

function clearAllFilters(viewKey, { keepMenuOpen = true } = {}) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings) {return;}
  setOpenMenuState(keepMenuOpen ? { viewKey, menuId: "filter" } : null);
  settings.filters = {};
  saveViewSettings();
  rerenderBoard();
}

function clearSearch(viewKey, { clearFilters = false } = {}) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings) {return;}
  queueSearchFocusRestore(viewKey);
  settings.searchDraft = "";
  settings.searchQuery = "";
  if (clearFilters) {
    settings.filters = {};
  }
  const existingTimer = searchDebounceTimers.get(viewKey);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }
  searchDebounceTimers.delete(viewKey);
  rerenderBoard();
}

function applySearch(viewKey, { force = false } = {}) {
  const settings = getBoardViewSettings(viewKey);
  if (!settings) {return;}
  queueSearchFocusRestore(viewKey);

  const nextDraft = (settings.searchDraft || "").trim();
  if (!nextDraft) {
    if (!settings.searchQuery) {return;}
    settings.searchQuery = "";
    rerenderBoard();
    return;
  }

  if (!force && nextDraft.length < 3) {return;}

  if (!settings.searchQuery && getTotalActiveFilterCount(viewKey) > 0) {
    settings.filters = {};
  }

  if (settings.searchQuery === nextDraft) {return;}
  settings.searchQuery = nextDraft;
  rerenderBoard();
}

function queueSearchApply(viewKey) {
  const previousTimer = searchDebounceTimers.get(viewKey);
  if (previousTimer) {
    clearTimeout(previousTimer);
  }

  const timerId = setTimeout(() => {
    applySearch(viewKey);
    searchDebounceTimers.delete(viewKey);
  }, 220);
  searchDebounceTimers.set(viewKey, timerId);
}

function updateSearchFieldState(searchInput) {
  const wrapper = searchInput?.closest("[data-search-wrapper]");
  const clearButton = wrapper?.querySelector("[data-control-action=\"clear-search\"]");
  const icon = wrapper?.querySelector("[data-search-icon]");
  const isActive = Boolean(searchInput?.value?.trim());

  wrapper?.classList.toggle("border-2", isActive);
  wrapper?.classList.toggle(ACTIVE_BORDER_CLASS, isActive);
  wrapper?.classList.toggle("border", !isActive);
  wrapper?.classList.toggle("border-gray-200", !isActive);
  icon?.classList.toggle(ACTIVE_TEXT_CLASS, isActive);
  icon?.classList.toggle("text-gray-500", !isActive);
  clearButton?.classList.toggle("hidden", !isActive);
}

function populateMenu(button) {
  if (!button) {return;}
  const menuId = button.dataset.controlTrigger;
  const viewKey = button.dataset.viewKey;
  const menu = button.closest("[data-control-wrapper]")?.querySelector(
    "[data-control-menu]",
  );
  if (!menu || !viewKey) {return;}

  menu.innerHTML = renderControlMenu(menuId, viewKey);
  if (!menu.classList.contains("hidden")) {
    queueControlMenuPosition(button);
  }
}

function reopenStoredMenu(viewKey, controls) {
  const openMenuState = getOpenMenuState();
  if (!openMenuState || openMenuState.viewKey !== viewKey) {return;}

  const button = controls.querySelector(
    `[data-control-trigger="${openMenuState.menuId}"]`,
  );
  if (!button) {
    clearOpenMenuState();
    return;
  }

  populateMenu(button);
  applyControlButtonActiveState(button, true);
  button.closest("[data-control-wrapper]")?.querySelector("[data-control-menu]")
    ?.classList.remove("hidden");
  queueControlMenuPosition(button);
}

/**
 * Renders the runtime control bar for a board view.
 * @param {string} viewKey Board view key.
 * @returns {void}
 */
export function renderBoardControlBar(viewKey) {
  const controls = document.getElementById(`${getItemType(viewKey)}-board-controls`);
  const settings = getBoardViewSettings(viewKey);
  if (!controls || !settings) {return;}
  applyBrandingVariables(controls, appData);

  const layout = getEffectiveViewLayout(viewKey);
  const showColumnsControl = layout === "table";
  const isMobileLayout = window.matchMedia?.(MOBILE_BOARD_LAYOUT_MEDIA)
    ?.matches === true;

  controls.classList.remove("hidden");
  controls.innerHTML = `
    <div class="board-controls-shell flex items-center gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:justify-end">
      <div
        class="flex h-10 min-w-0 flex-1 items-center rounded-full border border-gray-200 bg-white px-3 text-gray-500 transition-colors sm:w-[17rem] sm:flex-none sm:px-4"
        data-search-wrapper
      >
        <span class="material-symbols-outlined mr-2 text-[20px] text-gray-500" data-search-icon>search</span>
        <input
          type="text"
          class="board-search-input min-w-0 flex-1 truncate bg-transparent text-sm text-gray-700 outline-none placeholder:truncate placeholder:text-gray-400"
          placeholder="Search this board"
          data-view-key="${viewKey}"
          value="${escapeHtml(settings.searchDraft || "")}"
        >
        <button
          type="button"
          class="hidden inline-flex shrink-0 items-center justify-center self-center text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)]"
          data-control-action="clear-search"
        >
          <span class="material-symbols-outlined block text-[18px] leading-none">close</span>
        </button>
      </div>
      <div class="flex shrink-0 items-center justify-end gap-1.5 sm:flex-wrap sm:gap-2">
        ${isMobileLayout ? "" : renderActionButton({
          viewKey,
          action: "toggle-layout",
          icon: getLayoutToggleIcon(viewKey),
        })}
        ${renderMenuButton({
          viewKey,
          menuId: "group",
          icon: CONTROL_ICON_MAP.group,
        })}
        ${renderMenuButton({
          viewKey,
          menuId: "sort",
          icon: CONTROL_ICON_MAP.sort,
          active: Boolean(settings.sortBy),
          clearable: true,
        })}
        ${renderMenuButton({
          viewKey,
          menuId: "filter",
          icon: CONTROL_ICON_MAP.filter,
          active: getTotalActiveFilterCount(viewKey) > 0,
          badgeCount: getTotalActiveFilterCount(viewKey),
          clearable: true,
        })}
        ${renderMenuButton({
          viewKey,
          menuId: "columns",
          icon: CONTROL_ICON_MAP.columns,
          hidden: !showColumnsControl,
        })}
        <button
          type="button"
          class="add-item-btn inline-flex h-10 w-10 items-center justify-center rounded-full bg-[var(--dfc-brand-primary)] text-[var(--dfc-brand-primary-contrast)] shadow-sm transition-colors hover:bg-[var(--dfc-brand-primary-hover)]"
          data-type="${getItemType(viewKey)}"
        >
          <span class="material-symbols-outlined pointer-events-none text-[22px]">add</span>
        </button>
      </div>
    </div>
  `;

  const searchInput = controls.querySelector(".board-search-input");
  if (searchInput) {
    updateSearchFieldState(searchInput);
  }
  reopenStoredMenu(viewKey, controls);
  restorePendingSearchFocus(viewKey, controls);
}

/**
 * Installs delegated event handlers for all board control bars.
 * @returns {void}
 */
export function initBoardControlBarListeners() {
  if (controlsInitialised) {return;}
  controlsInitialised = true;

  if (!hasMobileLayoutListener) {
    const mobileLayoutQuery = window.matchMedia?.(MOBILE_BOARD_LAYOUT_MEDIA);
    const handleLayoutChange = () => {
      clearOpenMenuState();
      rerenderBoard();
    };

    if (mobileLayoutQuery?.addEventListener) {
      mobileLayoutQuery.addEventListener("change", handleLayoutChange);
    } else if (mobileLayoutQuery?.addListener) {
      mobileLayoutQuery.addListener(handleLayoutChange);
    }
    hasMobileLayoutListener = true;
  }

  document.addEventListener("mousedown", (event) => {
    const clearSearchButton = event.target.closest("[data-control-action=\"clear-search\"]");
    if (!clearSearchButton) {return;}

    event.preventDefault();
    event.stopPropagation();
    clearSearch(clearSearchButton.closest(".board-controls-shell")
      ?.querySelector(".board-search-input")?.dataset.viewKey, { clearFilters: true });
  });

  document.addEventListener("click", (event) => {
    const clearControlButton = event.target.closest("[data-control-clear]");
    if (clearControlButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const menuId = clearControlButton.dataset.controlClear;
      const viewKey = clearControlButton.dataset.viewKey;
      if (menuId === "sort") {
        clearSort(viewKey, { keepMenuOpen: false });
      } else if (menuId === "filter") {
        clearAllFilters(viewKey, { keepMenuOpen: false });
      }
      return;
    }

    const clearSearchButton = event.target.closest("[data-control-action=\"clear-search\"]");
    if (clearSearchButton) {
      clearSearch(clearSearchButton.closest(".board-controls-shell")
        ?.querySelector(".board-search-input")?.dataset.viewKey, { clearFilters: true });
      return;
    }

    const layoutToggleButton = event.target.closest(
      "[data-control-action=\"toggle-layout\"]",
    );
    if (layoutToggleButton) {
      const viewKey = layoutToggleButton.dataset.viewKey;
      const nextLayout = getViewLayout(viewKey) === "table" ? "board" : "table";
      clearOpenMenuState();
      updateLayout(viewKey, nextLayout);
      return;
    }

    const groupOption = event.target.closest("[data-group-option]");
    if (groupOption) {
      updateGrouping(groupOption.dataset.viewKey, groupOption.dataset.groupOption);
      return;
    }

    const groupBackButton = event.target.closest("[data-group-back]");
    if (groupBackButton) {
      setGroupMenuState(groupBackButton.dataset.groupBack);
      populateMenu(groupBackButton.closest("[data-control-menu]")?.parentElement?.querySelector("[data-control-trigger]"));
      return;
    }

    const dateModeOption = event.target.closest("[data-date-mode-option]");
    if (dateModeOption) {
      updateDateGroupingMode(
        dateModeOption.dataset.viewKey,
        dateModeOption.dataset.dateModeOption,
      );
      return;
    }

    const sortOption = event.target.closest("[data-sort-option]");
    if (sortOption) {
      updateSort(sortOption.dataset.viewKey, sortOption.dataset.sortOption);
      return;
    }

    const sortClearButton = event.target.closest("[data-sort-clear]");
    if (sortClearButton) {
      clearSort(sortClearButton.dataset.sortClear);
      return;
    }

    const filterCategoryButton = event.target.closest("[data-filter-category]");
    if (filterCategoryButton) {
      setFilterMenuState(
        filterCategoryButton.dataset.viewKey,
        filterCategoryButton.dataset.filterCategory,
      );
      populateMenu(filterCategoryButton.closest("[data-control-wrapper]")?.querySelector("[data-control-trigger]"));
      return;
    }

    const filterBackButton = event.target.closest("[data-filter-back]");
    if (filterBackButton) {
      setFilterMenuState(filterBackButton.dataset.filterBack, null);
      populateMenu(filterBackButton.closest("[data-control-menu]")?.parentElement?.querySelector("[data-control-trigger]"));
      return;
    }

    const filterClearAllButton = event.target.closest("[data-filter-clear-all]");
    if (filterClearAllButton) {
      clearAllFilters(filterClearAllButton.dataset.filterClearAll);
      return;
    }

    const filterClearCategoryButton = event.target.closest("[data-filter-clear-category]");
    if (filterClearCategoryButton) {
      updateFilterValues(
        filterClearCategoryButton.dataset.viewKey,
        filterClearCategoryButton.dataset.filterClearCategory,
        [],
      );
      return;
    }

    const filterDateApplyButton = event.target.closest("[data-filter-date-apply]");
    if (filterDateApplyButton) {
      const viewKey = filterDateApplyButton.dataset.viewKey;
      const filterKey = filterDateApplyButton.dataset.filterDateApply;
      const menu = filterDateApplyButton.closest("[data-control-menu]");
      const fromValue = menu?.querySelector(`[data-filter-date-from="${filterKey}"]`)?.value || "";
      const toValue = menu?.querySelector(`[data-filter-date-to="${filterKey}"]`)?.value || "";
      updateFilterValues(viewKey, filterKey, [fromValue, toValue]);
      return;
    }

    const filterDateClearButton = event.target.closest("[data-filter-date-clear]");
    if (filterDateClearButton) {
      updateFilterValues(
        filterDateClearButton.dataset.viewKey,
        filterDateClearButton.dataset.filterDateClear,
        ["", ""],
      );
      return;
    }

    const menuButton = event.target.closest("[data-control-trigger]");
    if (menuButton) {
      populateMenu(menuButton);
      openControlMenu(menuButton);
      return;
    }

    if (!event.target.closest(".board-controls-shell")) {
      closeAllControlMenus();
    }
  });

  document.addEventListener("change", (event) => {
    const columnCheckbox = event.target.closest(".board-column-checkbox");
    if (columnCheckbox) {
      updateVisibleColumn(
        columnCheckbox.dataset.viewKey,
        columnCheckbox.dataset.columnKey,
        columnCheckbox.checked,
      );
      return;
    }

    const filterCheckbox = event.target.closest(".board-filter-checkbox");
    if (filterCheckbox) {
      const viewKey = filterCheckbox.dataset.viewKey;
      const filterKey = filterCheckbox.dataset.filterKey;
      const settings = getBoardViewSettings(viewKey);
      const currentValues = Array.isArray(settings?.filters?.[filterKey]) ?
        [...settings.filters[filterKey]] : [];
      const nextValues = filterCheckbox.checked ?
        [...new Set([...currentValues, filterCheckbox.value])] :
        currentValues.filter((value) => value !== filterCheckbox.value);
      updateFilterValues(viewKey, filterKey, nextValues);
    }
  });

  document.addEventListener("input", (event) => {
    const searchInput = event.target.closest(".board-search-input");
    if (searchInput) {
      const viewKey = searchInput.dataset.viewKey;
      const settings = getBoardViewSettings(viewKey);
      if (!settings) {return;}
      settings.searchDraft = searchInput.value;
      updateSearchFieldState(searchInput);
      queueSearchFocusRestore(viewKey, searchInput);
      queueSearchApply(viewKey);
    }
  });

  document.addEventListener("keydown", (event) => {
    const searchInput = event.target.closest(".board-search-input");
    if (!searchInput) {return;}
    if (event.key === "Enter") {
      event.preventDefault();
      applySearch(searchInput.dataset.viewKey, { force: true });
    }
  });

  document.addEventListener("focusout", (event) => {
    const searchInput = event.target.closest(".board-search-input");
    if (!searchInput) {return;}
    applySearch(searchInput.dataset.viewKey, { force: true });
  });

  window.addEventListener("resize", positionVisibleControlMenus);
  document.addEventListener("scroll", positionVisibleControlMenus, true);
}
